<script setup lang="ts">
/**
 * Singapura — AR Active Declaration Statement. 원본 서식 재현.
 *   스칼라 = CddVarField(aura/agm cdd 재사용), 인라인 select/조항 = doc-toggle(값 저장).
 *   조건값은 백엔드 [[if …]] 가 소비(transfers_status/transfers_since/fs_audited/fs_by_agm/is_solvent/is_*_exempt).
 */
import CddVarField from '../../aura/agm/cdd/CddVarField.vue';
import { useAgmFill } from '../../aura/agm/cdd/useCddFill';
import '../../aura/agm/docs/agmDoc.css';

const fill = useAgmFill();
function val(k: string) { return String(fill.values[k] ?? '').trim(); }
function setSel(k: string, v: string) { fill.setValue(k, val(k) === v ? '' : v); }
function yn(k: string) { const v = val(k).toLowerCase(); return v === 'yes' || v === 'true' || v === '1' ? 'yes' : v === 'no' || v === 'false' || v === '0' ? 'no' : ''; }
function setYn(k: string, v: 'yes' | 'no') { fill.setValue(k, yn(k) === v ? '' : v); }
function on(k: string) { const v = val(k).toLowerCase(); return v === 'yes' || v === 'true' || v === '1'; }
function toggle(k: string) { fill.setValue(k, on(k) ? 'no' : 'yes'); }
</script>

<template>
  <div class="agm-doc">
    <!-- Header -->
    <table class="cdd-tbl" style="width:auto;margin-bottom:8px">
      <tbody>
        <tr><td class="cdd-lcell">Name of Company</td><td>:&nbsp; <CddVarField field-key="company_name" label="Company Name" /></td></tr>
        <tr><td class="cdd-lcell">Unique Entity Number</td><td>:&nbsp; <CddVarField field-key="company_uen" label="UEN" /></td></tr>
        <tr><td class="cdd-lcell">For the financial year ended</td><td>:&nbsp; <CddVarField field-key="financial_year_end" label="FYE" /></td></tr>
      </tbody>
    </table>

    <p class="doc-p">I, the undersigned officer of the abovementioned company, hereby declare that:</p>

    <!-- 원본과 같은 번호 매기기: 진술 항목 (i)~(x) 소문자 로마숫자, "Please click applicable option" 아래 두 선택지는 "o" 글머리.
         ⚠ 각 항목은 [번호 칸 | 본문 한 덩어리] 2칸 grid — 본문을 한 <span> 으로 묶어야 날짜 칸(CddVarField)이
           문장 중간으로 튀어나가지 않는다(예전 display:flex 가 텍스트·칸을 각각 열로 갈라 날짜가 엉뚱한 위치로 보였음). -->
    <p class="doc-p num-item"><span class="num">(i)</span><span class="body">I have verified the particulars of the abovementioned company in this Annual Return are accurate and up to date as at
      <CddVarField field-key="verification_date" label="Verification date" /> (date must not be more than 14 days before the date of lodgment);</span></p>

    <p class="doc-p num-item"><span class="num">(ii)</span><span class="body">
      I have made an inspection of the company’s share register and confirmed that transfers
      <span class="doc-toggle" :class="{ off: val('transfers_status') !== 'registered' }" @click="setSel('transfers_status','registered')">have been registered</span> /
      <span class="doc-toggle" :class="{ off: val('transfers_status') !== 'not_registered' }" @click="setSel('transfers_status','not_registered')">have not been registered</span> /
      <span class="doc-toggle" :class="{ off: val('transfers_status') !== 'none' }" @click="setSel('transfers_status','none')">have not taken place</span>
      since the date of
      <span class="doc-toggle" :class="{ off: val('transfers_since') !== 'last_ar' }" @click="setSel('transfers_since','last_ar')">the last annual return</span> /
      <span class="doc-toggle" :class="{ off: val('transfers_since') !== 'incorporation' }" @click="setSel('transfers_since','incorporation')">incorporation</span>
      of the company; and
    </span></p>

    <p class="doc-p num-item"><span class="num">(iii)</span><span class="body"><b>Please click applicable option:</b></span></p>

    <p class="doc-p opt" :class="{ dim: !on('is_exempt_private_company') }">
      <span class="bullet">o</span>
      <span class="doc-toggle" :class="{ off: !on('is_exempt_private_company') }" @click="toggle('is_exempt_private_company')">{{ on('is_exempt_private_company') ? '☑' : '☐' }}</span>
      <span class="body">For the entire financial year concerned, the company had been exempt private company at all relevant times as defined under Section 4(I) of the Singapore Companies Act 1967 by virtue of its being a private company of which no beneficial interest in its shares is held, directly or indirectly, by any corporation and having not more than 20 members;</span>
    </p>

    <p class="doc-p opt" :class="{ dim: !on('is_gazetted_private_exempt') }">
      <span class="bullet">o</span>
      <span class="doc-toggle" :class="{ off: !on('is_gazetted_private_exempt') }" @click="toggle('is_gazetted_private_exempt')">{{ on('is_gazetted_private_exempt') ? '☑' : '☐' }}</span>
      <span class="body">The company is gazette as or deemed to be a private exempt company for the purpose of the Companies Act;</span>
    </p>

    <p class="doc-p num-item"><span class="num">(iv)</span><span class="body">
      a / an
      <span class="doc-toggle" :class="{ off: yn('fs_audited') !== 'no' }" @click="setYn('fs_audited','no')">unaudited</span> /
      <span class="doc-toggle" :class="{ off: yn('fs_audited') !== 'yes' }" @click="setYn('fs_audited','yes')">audited</span>
      financial statements made up to the date stated in this return which comply with the requirements of the Companies Act have been presented before the company
      <span class="doc-toggle" :class="{ off: yn('fs_by_agm') !== 'yes' }" @click="setYn('fs_by_agm','yes')">in the annual general meeting</span> /
      <span class="doc-toggle" :class="{ off: yn('fs_by_agm') !== 'no' }" @click="setYn('fs_by_agm','no')">by way of a resolution by written means</span>,
      on the date stated in this annual return;
    </span></p>

    <p class="doc-p num-item"><span class="num">(v)</span><span class="body">
      as at the date that the financial statements for the financial year has been made up, the company appeared to be
      <span class="doc-toggle" :class="{ off: yn('is_solvent') !== 'yes' }" @click="setYn('is_solvent','yes')">able</span> /
      <span class="doc-toggle" :class="{ off: yn('is_solvent') !== 'no' }" @click="setYn('is_solvent','no')">unable</span>
      to meet its liabilities as and when they fall due;
    </span></p>

    <p class="doc-p num-item opt-num" :class="{ dim: !on('is_small_company_exempt') }">
      <span class="num">(vi)</span>
      <span class="body">
        <span class="doc-toggle" :class="{ off: !on('is_small_company_exempt') }" @click="toggle('is_small_company_exempt')">{{ on('is_small_company_exempt') ? '☑' : '☐' }}</span>
        the company is a small company/group exempt from audit for its financial year ended
        <CddVarField field-key="financial_year_end" label="FYE" /> since it meets the requirements of not exceeding consolidated total revenue/assets of $10 million and /or 50 employees for the period and in the manner stated in Section 205C of the Singapore Companies Act 1967;
      </span>
    </p>

    <p class="doc-p num-item"><span class="num">(vii)</span><span class="body">the company is not required to obtain an audit of its accounts by any notice from its members under Section 205C(2) of the Singapore Companies Act 1967;</span></p>
    <p class="doc-p num-item"><span class="num">(viii)</span><span class="body">the financial statements (if prepared) have been prepared in accordance with Part VI of the Singapore Companies Act 1967; and</span></p>
    <p class="doc-p num-item"><span class="num">(ix)</span><span class="body">the financial information in XBRL format (if filed) is accurate; and</span></p>
    <p class="doc-p num-item"><span class="num">(x)</span><span class="body">the accounting and other records required to be kept by the company have been kept in accordance with Section 199 of the Singapore Companies Act 1967.</span></p>

    <!-- Signature -->
    <div class="sign-block" style="margin-top:20px">
      <div class="sign-line">
        <CddVarField field-key="director_name" label="Name of Director" /><br />
        ______________________________<br />
        Name and Signature of Officer
      </div>
    </div>
    <p class="doc-p">Designation in the abovementioned company: Director</p>
    <p class="doc-p">* Nric / Passport / Fin No: <CddVarField field-key="director_id_no" label="NRIC/Passport/FIN" /></p>
    <p class="doc-p">Date: <CddVarField field-key="declaration_date" label="Date" /></p>
    <p class="doc-note">* Delete whichever is inapplicable</p>
  </div>
</template>

<style scoped>
/* 번호 항목 = [번호 | 본문] 2칸. 본문은 한 덩어리(span.body)라 문장과 입력칸이 갈라지지 않는다. */
.num-item { display: grid; grid-template-columns: 40px 1fr; align-items: start; }
.num-item .num { color: #333; }
/* 선택지 = [o 글머리 | 체크 | 본문] — 원본처럼 (iii) 아래로 들여쓰기 */
.opt { display: grid; grid-template-columns: 18px 22px 1fr; align-items: start; padding-left: 40px; }
.opt.dim, .opt-num.dim { opacity: 0.5; }
</style>
